import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "@mui/material";
import StockDesign from "./StockDesign";
import SortContext from "context/SortContext";
import LoadingSmallCard from "components/ui/Skeleton/LoadingSmallCard";
import { useStocks } from "../api/get-stocks";
import { useFilter } from "context/FilterContext";
import { useGlobalContext } from "context/GlobalContext";
import apiClient from "lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import Product from "features/Stock/StockProd/components/Product";
import DataNotFound from "components/ui/datanotfound/DataNotFound";
import { useLocation } from "react-router-dom";

const Stock = () => {
  const { selectedSortValue, sortOptions, search } = React.useContext(SortContext);
  const selectedOption = sortOptions.find((option) => option.id == selectedSortValue);
  const {
    setFilterChangeCount,
    setDisplayCount,
    setFilterChangeDesignCount,
    setSearchResultCount,
    dynamicfilterCount,
    setDynamicFilterCount,
    filterChangeCount,
    filterChangeDesignCount,
    setSearchSuffix,
  } = useGlobalContext();
  const firstRenderRef = useRef(true);

  const { filterValues } = useFilter();
  const selectedSortBY = selectedOption?.sortBy;
  const selectedSortOrder = selectedOption?.order;

  // const locationMapping = {
  //   "IDL": "Idel Inventory",
  //   "Rose Gold": "Pink",
  //   "White Gold": "White",
  // };

  // const Location = filterValues?.Location?.map((location) => locationMapping[location]) || [];

  const Location = filterValues?.Location?.map((location) =>
    location === "IDL" ? "Idel Inventory" : location
  ) || [];

  const stockStatus = filterValues["Stock Status"]?.[0] === "On Memo" ? "Memo" : filterValues["Stock Status"]?.[0] || "";

  // Client Design (CD-) stock shows only in Create Quote flow
  const location = useLocation();
  const isCreateQuoteFlow = Boolean(location.state?.innerChnages && location.state?.newName === "Create Order");

  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);

  const { data, refetch, isLoading, isError, error } = useStocks({
    limit: 30,
    sortBy: selectedSortBY,
    order: selectedSortOrder,
    searchTerm: search,
    location: Location,
    stockStatus: stockStatus,
    // stockStatus: "All",
    includeClientDesign: isCreateQuoteFlow,
  });

  const fetchStockData = async () => {
    setIsSyncing(true); // 🚀 sync start
    queryClient.removeQueries(["stocks", "stockDesigns", "stockSetDesigns", "substocks", "subStockSetDesigns"]);

    try {
      await apiClient.get("/api/v1/stocks/sync-stockcollection");

      await queryClient.refetchQueries(["stocks"]);
    } catch (err) {
      console.error("Sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    fetchStockData();
  }, []);

  useEffect(() => {
    if (search === "") {
      setDynamicFilterCount(true);
    }
    if (dynamicfilterCount) {
      setFilterChangeCount(data?.pages[0]?.totalCount);
      setFilterChangeDesignCount(data?.pages[0]?.totalStyles);
      setSearchResultCount(data?.pages[0]?.totalCount);
    } else {
      setSearchResultCount(data?.pages[0]?.totalCount);
      setFilterChangeCount(filterChangeCount);
      setFilterChangeDesignCount(filterChangeDesignCount);
    }
    setDisplayCount(true);
    setSearchSuffix("Articles");
    return () => {
      setSearchResultCount(0);
      setFilterChangeCount(0);
      setFilterChangeDesignCount(0);
      setDisplayCount(false);
      setSearchSuffix("Designs");
    };
  }, [data]);

  if (isLoading || isSyncing) return <LoadingSmallCard count={30} />;
  if (isError) return <div>Error: {error?.message}</div>;

  const hasNoResults =
    search &&
    data?.pages?.every(
      (page) => !page?.categories?.length && !page?.cardData?.length
    );

  if (hasNoResults) return <DataNotFound message={`No results found for "${search}"`} />;

  return (
    <>
      <Grid
        container
        className="Grid-padding"
        direction="row"
        justifyContent="flex-start"
        sx={{ flex: "1 1 1", display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "30px", rowGap: "32px" }}
      >
        {data?.pages?.map((page) => (
          <>
            {/* Render categories */}
            {page?.categories?.map((category) => (
              <Grid item key={category.id}>
                <StockDesign category={category} />
              </Grid>
            ))}

            {/* Render cardData */}
            {page?.cardData?.map((design) => {
              return <Product product={design} key={design._id} />;
            })}
          </>
        ))}
      </Grid>
    </>
  );
};

export default Stock;
